import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["error"],
  _excluded2 = ["error"];
import { useMemo } from 'react';
import { skipToken } from '@apollo/client';
import { useSuspenseQuery } from 'data-fetching-client';
import { registerQuery } from 'data-fetching-client/next';
import http from 'hub-http/clients/apiClient';
import { createRpcClientV2 } from 'rpc-client-utils';
import { getUsersByIdsRpc } from '../../../__generated__/chirp/com/hubspot/appusers/rpc/UsersRpc';
import { useGetUserIdsOnHubByRoleIdAndPendingStatusSuspenseQuery } from '../../../__generated__/chirp/com/hubspot/appusers/rpc/UsersRpc.hooks';
import { INTEGRATIONS_MANAGEMENT_ROLE_ID, SUPER_ADMIN_ROLE_ID } from '../../constants/roles';
import { chunk } from '../../utils/chunk';
import { useCaptureError } from '../useCaptureError';
const USER_IDS_BATCH_SIZE = 100;
const chirpClient = createRpcClientV2({
  hubHttpClient: http
});
const BATCHED_GET_USERS_BY_IDS_QUERY = registerQuery({
  fieldName: 'batched_getUsersByIds',
  args: ['userIds'],
  fetcher(request) {
    var _request$userIds;
    const userIds = (_request$userIds = request.userIds) !== null && _request$userIds !== void 0 ? _request$userIds : [];
    if (userIds.length <= USER_IDS_BATCH_SIZE) {
      return chirpClient.call(getUsersByIdsRpc, {
        request
      });
    }
    const batches = chunk(userIds, USER_IDS_BATCH_SIZE);
    return Promise.all(batches.map(batchIds => chirpClient.call(getUsersByIdsRpc, {
      request: {
        userIds: batchIds
      }
    }))).then(responses => ({
      users: responses.flatMap(r => r.users)
    }));
  }
});
const userIdsToUserMap = users => {
  return users.reduce((acc, user) => {
    acc[user.id] = user;
    return acc;
  }, {});
};
const useFetchUsers = userIds => {
  const _useSuspenseQuery = useSuspenseQuery(BATCHED_GET_USERS_BY_IDS_QUERY, {
      variables: userIds === undefined ? skipToken : {
        userIds
      }
    }),
    {
      error
    } = _useSuspenseQuery,
    rest = _objectWithoutPropertiesLoose(_useSuspenseQuery, _excluded);
  useCaptureError(error, {
    message: 'Error fetching users by ids'
  });
  return Object.assign({
    error
  }, rest);
};
const useFetchUsersByRoleId = roleId => {
  const _useGetUserIdsOnHubBy = useGetUserIdsOnHubByRoleIdAndPendingStatusSuspenseQuery({
      variables: {
        roleId,
        includePending: false,
        includeInactive: false
      }
    }),
    {
      error
    } = _useGetUserIdsOnHubBy,
    rest = _objectWithoutPropertiesLoose(_useGetUserIdsOnHubBy, _excluded2);
  useCaptureError(error, {
    message: 'Error fetching users by role id',
    extra: {
      roleId
    }
  });
  return Object.assign({
    error
  }, rest);
};
function useFetchSuperAdminsUsers() {
  const {
    data
  } = useFetchUsersByRoleId(SUPER_ADMIN_ROLE_ID);
  return useFetchUsers(data === null || data === void 0 ? void 0 : data.userIds);
}
function useFetchIntegrationsManagementUsers() {
  const {
    data
  } = useFetchUsersByRoleId(INTEGRATIONS_MANAGEMENT_ROLE_ID);
  return useFetchUsers(data === null || data === void 0 ? void 0 : data.userIds);
}
export function useFetchUsersWithInstallAccess() {
  const {
    data: _superAdmins
  } = useFetchSuperAdminsUsers();
  const {
    data: _integrationsManagementWriteUsers
  } = useFetchIntegrationsManagementUsers();
  const superAdmins = useMemo(() => userIdsToUserMap(_superAdmins === null || _superAdmins === void 0 ? void 0 : _superAdmins.result.users), [_superAdmins]);
  const integrationsManagementWriteUsers = useMemo(() => userIdsToUserMap(_integrationsManagementWriteUsers === null || _integrationsManagementWriteUsers === void 0 ? void 0 : _integrationsManagementWriteUsers.result.users), [_integrationsManagementWriteUsers]);
  return {
    superAdmins,
    integrationsManagementWriteUsers
  };
}